import { AuthService } from '../auth/AuthService'
import { _queryParameterize } from '../script-utils'

export const HttpService = new class {
	get<T = unknown>(url: string, options: HttpOptions = {}) {
		const paramaterizedUrl = url + _queryParameterize(options.params || {})

		return this.performRequest<T>(
			paramaterizedUrl,
			{
				method: 'GET',
				headers: options.headers || {},
			},
			options.authenticate,
		)
	}

	put<T = unknown>(url: string, body: string | object, options: HttpOptions = {}) {
		const paramaterizedUrl = url + _queryParameterize(options.params || {})
		const isJson = typeof body !== 'string'
		const acceptHeader = isJson ? 'application/json' : 'text/plain'
		const contentType = isJson ? 'application/json' : 'text/plain'
		const bodyContent = (isJson ? JSON.stringify(body) : body) as string

		return this.performRequest<T>(
			paramaterizedUrl,
			{
				method: 'PUT',
				headers: { ...options.headers, 'accept': acceptHeader, 'content-type': contentType },
				body: bodyContent,
			},
			options.authenticate,
		)
	}

	post<T = unknown>(url: string, body: string | object, options: HttpOptions = {}) {
		const paramaterizedUrl = url + _queryParameterize(options.params || {})
		const isJson = typeof body !== 'string'
		const acceptHeader = isJson ? 'application/json' : 'text/plain'
		const contentType = isJson ? 'application/json' : 'text/plain'
		const bodyContent = (isJson ? JSON.stringify(body) : body) as string

		return this.performRequest<T>(
			paramaterizedUrl,
			{
				method: 'POST',
				headers: { ...options.headers, 'accept': acceptHeader, 'content-type': contentType },
				body: bodyContent,
			},
			options.authenticate,
		)
	}

	delete<T = unknown>(url: string, options: HttpOptions = {}) {
		const paramaterizedUrl = url + _queryParameterize(options.params || {})

		return this.performRequest<T>(
			paramaterizedUrl,
			{
				method: 'DELETE',
				headers: options.headers || {},
			},
			options.authenticate,
		)
	}

	private async performRequest<T>(
		url: string,
		requestOpts: RequestOptions,
		includeAuthHeader: boolean = false,
	): Promise<T> {
		const requestInit: RequestInit = { ...requestOpts }

		if (includeAuthHeader) {
			requestInit.headers = {
				...requestOpts.headers,
				Authorization: 'Bearer ' + (await AuthService.getAccessToken()),
			}
		}

		return fetch(url, requestInit).then(async response => {
			if (response.ok) {
				return extractResponseBody(response)
			} else {
				return Promise.reject({
					status: response.status,
					statusText: response.statusText,
					message: await response.text(),
				})
			}
		})
	}
}()

const extractResponseBody = (response: Response) => {
	const contentHeader = response.headers.get('Content-Type')
	if (contentHeader && contentHeader.includes('application/json')) {
		return response.json()
	} else {
		return response.text()
	}
}

export interface HttpError {
    status: number
    statusText: string
    message: string
}

export function isHttpError(err: unknown, status?: number): err is HttpError {
    if (typeof err !== 'object' || err === null) return false
    if (!('status' in err) || typeof (err as HttpError).status !== 'number') return false
    if (status !== undefined) return (err as HttpError).status === status
    return true
}

interface HttpOptions {
    params?: Lookup<string | number>;
    headers?: Lookup<string>;
    authenticate?: boolean;
}

type RequestOptions =
    | {
          method: 'GET';
          headers: Lookup<string>;
      }
    | {
          method: 'POST';
          body: string;
          headers: Lookup<string>;
      }
    | {
          method: 'PUT';
          body: string;
          headers: Lookup<string>;
      }
    | {
          method: 'DELETE';
          headers: Lookup<string>;
      };
